import React, { FC } from 'react';

import { RoverLoading } from '@rover/react-lib/src/components';
import {
  apiSearchOptimizedRetrieve,
  getApiSearchOptimizedRetrieveQueryKey,
  useApiSearchOptimizedRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-search-optimized-retrieve';
import { captureMessage } from '@rover/rsdk/src/modules/ErrorReporting/sentry';
import { getPath } from '@rover/shared/js/utils/getFullUrl';
import { Http500Error } from '@rover/utilities/HttpError';
import { filterNullishProperties } from '@rover/utilities/object';

import MetaDiscountSmokeTest from '../../components/MetaDiscountSmokeTest';
import SeoLocationPageTemplate from '../../templates/SeoLocationPageTemplate/SeoLocationPageTemplate';
import { SeoLocationContainerProps, SeoLocationEngineContainer } from '../../types';
import { getTopNeighborhoodServices } from '../../utils';
import { handleSeoLocationQueryError } from '../../utils/handleSeoLocationQueryError';
import { getApiSearchSeoLocationFaqsListPrefetchQueryOptions } from '../../utils/useSeoLocationHooks';
import useSeoLocationPageData from '../../utils/useSeoLocationPageData';

const SeoLocationContainer: FC<SeoLocationContainerProps> = ({
  seoLocation,
  seoLocationDescriptor,
}) => {
  const {
    reviews,
    countryConfig,
    frontendConfig,
    serviceTypes,
    isLoading: isLoadingPageData,
    isError: isErrorPageData,
    queryTags,
  } = useSeoLocationPageData(seoLocationDescriptor);

  const searchOptimizedRetrieveParams = filterNullishProperties(seoLocation.details.searchParams);
  const {
    isLoading: isLoadingSearch,
    isError: isErrorSearch,
    data: searchResponse,
    error: errorSearch,
  } = useApiSearchOptimizedRetrieve(searchOptimizedRetrieveParams);

  if (isErrorPageData || isErrorSearch) {
    // 410s should be handled during prefetch, but just in case, also handle them here
    if (errorSearch) {
      handleSeoLocationQueryError(errorSearch);
    }

    captureMessage('Unhandled error loading SeoLocationPage', {
      tags: { pathname: getPath(), ...queryTags, isErrorSearch },
    });
    throw new Http500Error();
  }

  const isLoading = isLoadingPageData || isLoadingSearch;
  if (isLoading) {
    if (process.env.JS_ENV_CLIENT) {
      // We shouldn't ever show a loading screen in the browser
      captureMessage('SeoLocationPageContainer rendered with missing prefetches', {
        tags: { pathname: getPath(), ...queryTags, isLoadingSearch },
      });
    }

    return (
      <RoverLoading style={{ height: '100vh' }} data-testid="rover-loading" isLoading={isLoading} />
    );
  }

  // The checks above rule out a pending or failed query, so these have all landed — the
  // guard is what tells the compiler so.
  if (!reviews || !countryConfig || !frontendConfig || !serviceTypes || !searchResponse) {
    throw new Http500Error();
  }

  return (
    <>
      <MetaDiscountSmokeTest />
      <SeoLocationPageTemplate
        seoLocation={seoLocation}
        seoLocationDescriptor={seoLocationDescriptor}
        reviews={reviews.results!}
        countryConfig={countryConfig}
        frontendConfig={frontendConfig}
        serviceTypes={serviceTypes}
        topNeighborhoodServices={getTopNeighborhoodServices(seoLocation)}
        searchResponse={searchResponse}
      />
    </>
  );
};

const reactQueryPrefetch: SeoLocationEngineContainer['reactQueryPrefetch'] = (
  queryClient,
  { seoLocation, seoLocationDescriptor }
) => {
  const searchOptimizedRetrieveParams = filterNullishProperties(seoLocation.details.searchParams);

  return Promise.all([
    queryClient
      .fetchQuery({
        queryKey: getApiSearchOptimizedRetrieveQueryKey(searchOptimizedRetrieveParams),
        queryFn: () => apiSearchOptimizedRetrieve(searchOptimizedRetrieveParams),
      })
      .catch(handleSeoLocationQueryError),
    queryClient.prefetchQuery(
      getApiSearchSeoLocationFaqsListPrefetchQueryOptions(
        seoLocationDescriptor,
        seoLocation.localityType
      )
    ),
  ]);
};

export default Object.assign(SeoLocationContainer, { reactQueryPrefetch });
